import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { PremiumBanner } from "@/components/PremiumBanner";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, TrendingDown, RefreshCw } from "lucide-react";

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [checking, setChecking] = useState(false);

  const load = () => api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const checkDrops = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/price-drops/check");
      if (data.drops.length) {
        toast.success(`${data.drops.length} baisse(s) détectée(s)${data.email_enabled ? " — email envoyé" : ""}`);
      } else {
        toast.message("Aucune baisse de prix pour le moment");
      }
      if (!data.premium) toast.message("Passez à Premium pour recevoir les alertes par email");
      else if (!data.email_enabled) toast.message("Notifications email non configurées (clé Resend manquante)");
      load();
    } catch (e) { toast.error("Vérification impossible"); }
    finally { setChecking(false); }
  };

  const remove = async (pid) => {
    await api.delete(`/alerts/${pid}`);
    toast.success("Alerte supprimée");
    load();
  };

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-800 tracking-tight text-secondary">Alertes de prix</h1>
            <p className="mt-1 text-muted-foreground">Suivez vos produits favoris et soyez informé des baisses de prix par email.</p>
          </div>
          {alerts.length > 0 && (
            <Button onClick={checkDrops} disabled={checking} variant="outline" className="rounded-full" data-testid="check-drops-btn">
              <RefreshCw className={`mr-2 h-4 w-4 ${checking ? "animate-spin" : ""}`} /> Vérifier les baisses
            </Button>
          )}
        </div>

        <div className="mt-6"><PremiumBanner context="alerts" /></div>

        <div className="mt-8 space-y-3" data-testid="alerts-list">
          {alerts.map((a, i) => (
            <motion.div key={a.product_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="flex items-center justify-between rounded-xl border border-border bg-card p-5" data-testid={`alert-${a.product_id}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Bell className="h-5 w-5" /></div>
                <div>
                  <p className="font-semibold text-secondary">{a.name}</p>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground"><TrendingDown className="h-3 w-3 text-primary" /> Meilleur prix suivi : {euro(a.current_price)}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(a.product_id)} data-testid={`remove-alert-${a.product_id}`}>
                <BellOff className="mr-1 h-4 w-4" /> Retirer
              </Button>
            </motion.div>
          ))}
          {alerts.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-12 text-center">
              <BellOff className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <p className="text-muted-foreground">Aucune alerte pour l'instant.</p>
              <Link to="/comparateur"><Button variant="outline" className="mt-4 rounded-full" data-testid="go-comparateur-btn">Comparer un panier</Button></Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
